import React from "react";
import {
    Box,
    Typography,
    TextField,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Checkbox,
    FormGroup,
    FormLabel,
    Stack,
    Chip,
    Grid,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { Error, AttachFile, Link, AccessTime, CalendarToday } from "@mui/icons-material";
import { LayoutOptions, ItemLayoutConfig } from "./types";
import { QuestionnaireInputText } from "@/components/form/QuestionnaireInputText";
import { QuestionnaireTimeInput } from "@/components/form/QuestionnaireTimeInput";
import { FormTimeInput } from "@/components/form/FormTimeInput";

type Props = {
    item: any;
    level: number;
    answers: Record<string, any>;
    errors: Record<string, string>;
    touched: Record<string, boolean>;
    onAnswerChange: (linkId: string, value: any) => void;
    renderChild: (item: any, level: number) => React.ReactNode;
    layoutOptions?: LayoutOptions;
    itemLayoutConfig?: ItemLayoutConfig;
    shouldHideItem: (item: any) => boolean;
};

export const FormItem: React.FC<Props> = ({
    item,
    level,
    answers,
    errors,
    onAnswerChange,
    renderChild,
    layoutOptions,
    itemLayoutConfig,
    shouldHideItem,
}) => {
    const indent = level * 2;
    const error = errors[item.linkId];

    if (shouldHideItem(item)) {
        return null;
    }

    const effectiveColumns = Math.max(1, layoutOptions?.columns || 1);
    const gridUnit = 12 / effectiveColumns;
    const getItemColSpan = (linkId: string): number => {
        const span = itemLayoutConfig?.[linkId]?.colSpan;
        if (!span || span <= 0) return gridUnit;
        const total = Math.min(12, Math.max(gridUnit, span * gridUnit));
        return total;
    };
    const isHorizontal = (it: any): boolean => {
        const id = it.linkId;
        if (itemLayoutConfig?.[id]?.orientation) {
            return itemLayoutConfig[id].orientation === 'horizontal';
        }
        return (layoutOptions?.horizontalFields || []).includes(id);
    };
    const getLabelWidth = (it: any): number => {
        const id = it.linkId;
        if (itemLayoutConfig?.[id]?.labelWidthPx) return itemLayoutConfig[id].labelWidthPx as number;
        return layoutOptions?.labelWidthPx ?? 200;
    };
    const verticalSpacing = layoutOptions?.density === 'comfortable' ? 4 : 3;

    const renderWithOrientation = (control: React.ReactNode, helper?: React.ReactNode) => {
        const shouldShowLabel = ['coding', 'choice', 'boolean'].includes(item.type);

        if (isHorizontal(item)) {
            return (
                <Box key={item.linkId} data-linkid={item.linkId} sx={{ ml: indent, mb: verticalSpacing }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        {shouldShowLabel && (
                            <Box sx={{ width: getLabelWidth(item) }}>
                                <FormLabel component="legend" sx={{ mb: 0 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="subtitle2" fontWeight="500">
                                            {item.text}
                                        </Typography>
                                        {item.required && (
                                            <Typography variant="subtitle2" fontWeight="500" color="error">*</Typography>
                                        )}
                                    </Stack>
                                </FormLabel>
                            </Box>
                        )}
                        <Box sx={{ flex: 1 }}>
                            {control}
                            {helper}
                        </Box>
                    </Stack>
                </Box>
            );
        }
        return (
            <Box key={item.linkId} data-linkid={item.linkId} sx={{ ml: indent, mb: verticalSpacing }}>
                {shouldShowLabel && (
                    <FormLabel component="legend" sx={{ my: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2" fontWeight="500">
                                {item.text}
                            </Typography>
                            {item.required && (
                                <Typography variant="subtitle2" fontWeight="500" color="error">*</Typography>
                            )}
                        </Stack>
                    </FormLabel>
                )}
                {helper}
                {control}
            </Box>
        );
    };

    const wrapInGridIfNeeded = (node: React.ReactNode) => {
        if (effectiveColumns <= 1 || item.type === 'group') return node;
        return (
            <Grid item xs={getItemColSpan(item.linkId)} key={`grid-${item.linkId}`}>
                {node}
            </Grid>
        );
    };

    switch (item.type) {
        case 'group':
            return (
                <Box key={item.linkId} sx={{ ml: indent }}>
                    <Typography variant="subtitle2" fontWeight="500" p={1.5} mb={2} borderRadius={2} sx={{ bgcolor: 'neutral200.main', color: 'neutral700.main' }}>
                        {item.text}
                    </Typography>
                    {item.helpText && (
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            {item.helpText}
                        </Typography>
                    )}
                    {effectiveColumns > 1 ? (
                        <Grid container spacing={verticalSpacing}>
                            {item.item?.map((subItem: any) => renderChild(subItem, level + 1))}
                        </Grid>
                    ) : (
                        item.item?.map((subItem: any) => renderChild(subItem, level + 1))
                    )}
                </Box>
            );
        case 'text':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <QuestionnaireInputText
                        value={answers[item.linkId] || ''}
                        onChange={(value) => onAnswerChange(item.linkId, value)}
                        label={item.text}
                        placeholder="Digite sua resposta..."
                        multiline={item.maxLength > 100}
                        rows={item.maxLength > 100 ? 3 : 1}
                        required={item.required}
                        error={!!error}
                        helperText={error}
                        maxLength={item.maxLength}
                    />,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        case 'string':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <QuestionnaireInputText
                        value={answers[item.linkId] || ''}
                        onChange={(value) => onAnswerChange(item.linkId, value)}
                        label={item.text}
                        placeholder="Digite sua resposta..."
                        required={item.required}
                        error={!!error}
                        helperText={error}
                        maxLength={item.maxLength}
                    />,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        case 'coding':
        case 'choice':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <FormControl component="fieldset" fullWidth>
                        {item.repeats ? (
                            <FormGroup>
                                {item.answerOption?.map((option: any, index: number) => (
                                    <FormControlLabel
                                        key={index}
                                        control={
                                            <Checkbox
                                                checked={answers[item.linkId]?.includes(option.valueCoding?.code) || false}
                                                onChange={(e) => {
                                                    const currentAnswers = answers[item.linkId] || [];
                                                    if (e.target.checked) {
                                                        onAnswerChange(item.linkId, [...currentAnswers, option.valueCoding?.code]);
                                                    } else {
                                                        onAnswerChange(item.linkId, currentAnswers.filter((code: string) => code !== option.valueCoding?.code));
                                                    }
                                                }}
                                            />
                                        }
                                        label={option.valueCoding?.display || option.valueString}
                                    />
                                ))}
                            </FormGroup>
                        ) : (
                            <RadioGroup
                                value={answers[item.linkId] || ''}
                                onChange={(e) => onAnswerChange(item.linkId, e.target.value)}
                            >
                                {item.answerOption?.map((option: any, index: number) => (
                                    <FormControlLabel
                                        key={index}
                                        value={option.valueCoding?.code || option.valueString}
                                        control={<Radio />}
                                        label={option.valueCoding?.display || option.valueString}
                                    />
                                ))}
                            </RadioGroup>
                        )}
                    </FormControl>,
                    <>
                        {item.helpText && (
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                {item.helpText}
                            </Typography>
                        )}
                        {error && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                                <Error fontSize="small" sx={{ mr: 0.5 }} />
                                {error}
                            </Typography>
                        )}
                    </>
                )
            );
        case 'boolean':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                            value={answers[item.linkId] || ''}
                            onChange={(e) => onAnswerChange(item.linkId, e.target.value === 'true')}
                        >
                            <FormControlLabel value="true" control={<Radio />} label="Sim" />
                            <FormControlLabel value="false" control={<Radio />} label="Não" />
                        </RadioGroup>
                    </FormControl>,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        case 'date':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <QuestionnaireInputText
                        value={answers[item.linkId] || ''}
                        onChange={(value) => onAnswerChange(item.linkId, value)}
                        label={item.text}
                        placeholder=""
                        required={item.required}
                        error={!!error}
                        helperText={error}
                        sx={{
                            "& input": {
                                padding: "16px 14px",
                                fontSize: "0.875rem"
                            }
                        }}
                        InputProps={{
                            type: "date",
                            shrink: true
                        }}
                    />,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        case 'integer':
        case 'decimal':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <QuestionnaireInputText
                        value={answers[item.linkId] || ''}
                        onChange={(value) => onAnswerChange(item.linkId, value)}
                        label={item.text}
                        placeholder="Digite um número..."
                        required={item.required}
                        error={!!error}
                        helperText={error}
                        InputProps={{
                            type: "number"
                        }}
                    />,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        case 'display':
            return wrapInGridIfNeeded(
                <Box key={item.linkId} data-linkid={item.linkId} sx={{ ml: indent, mb: verticalSpacing }}>
                    <Typography variant="body1" sx={{
                        p: 2,
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200'
                    }}>
                        {item.text}
                    </Typography>
                    {item.helpText && (
                        <Typography variant="body2" color="text.secondary" mt={1}>
                            {item.helpText}
                        </Typography>
                    )}
                </Box>
            );
        case 'dateTime':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <QuestionnaireInputText
                        value={answers[item.linkId] || ''}
                        onChange={(value) => onAnswerChange(item.linkId, value)}
                        label={item.text}
                        placeholder=""
                        required={item.required}
                        error={!!error}
                        helperText={error}
                        sx={{
                            "& input": {
                                padding: "16px 14px",
                                fontSize: "0.875rem"
                            }
                        }}
                        InputProps={{
                            type: "datetime-local",
                            shrink: true,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <CalendarToday fontSize="small" color="action" />
                                </InputAdornment>
                            )
                        }}
                    />,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        case 'time':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <QuestionnaireTimeInput
                        value={answers[item.linkId] || ''}
                        onChange={(value) => onAnswerChange(item.linkId, value)}
                        label={item.text}
                        required={item.required}
                        error={!!error}
                        helperText={error}
                        format="HH:mm"
                    />,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        case 'url':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <QuestionnaireInputText
                        value={answers[item.linkId] || ''}
                        onChange={(value) => onAnswerChange(item.linkId, value)}
                        label={item.text}
                        placeholder="https://exemplo.com"
                        required={item.required}
                        error={!!error}
                        helperText={error}
                        InputProps={{
                            type: "url",
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Link fontSize="small" color="action" />
                                </InputAdornment>
                            )
                        }}
                    />,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        case 'attachment':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <Box>
                        <input
                            type="file"
                            id={`file-${item.linkId}`}
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    onAnswerChange(item.linkId, {
                                        contentType: file.type,
                                        data: file.name,
                                        title: file.name,
                                        size: file.size
                                    });
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            value={answers[item.linkId]?.title || ''}
                            label={item.text}
                            placeholder="Nenhum arquivo selecionado"
                            required={item.required}
                            error={!!error}
                            helperText={error}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => document.getElementById(`file-${item.linkId}`)?.click()}
                                            edge="end"
                                        >
                                            <AttachFile />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        case 'reference':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <QuestionnaireInputText
                        value={answers[item.linkId] || ''}
                        onChange={(value) => onAnswerChange(item.linkId, value)}
                        label={item.text}
                        placeholder="Digite a referência..."
                        required={item.required}
                        error={!!error}
                        helperText={error}
                    />,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        case 'quantity':
            return wrapInGridIfNeeded(
                renderWithOrientation(
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <QuestionnaireInputText
                            value={answers[item.linkId]?.value || ''}
                            onChange={(value) => {
                                const currentAnswer = answers[item.linkId] || {};
                                onAnswerChange(item.linkId, { ...currentAnswer, value });
                            }}
                            label={item.text}
                            placeholder="Digite a quantidade..."
                            required={item.required}
                            error={!!error}
                            helperText={error}
                            InputProps={{
                                type: "number"
                            }}
                            sx={{ flex: 1 }}
                        />
                        {item.unit && (
                            <TextField
                                value={answers[item.linkId]?.unit || ''}
                                onChange={(e) => {
                                    const currentAnswer = answers[item.linkId] || {};
                                    onAnswerChange(item.linkId, { ...currentAnswer, unit: e.target.value });
                                }}
                                label="Unidade"
                                placeholder="Ex: kg, cm, ml"
                                size="small"
                                sx={{ minWidth: 120 }}
                            />
                        )}
                    </Box>,
                    item.helpText ? (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {item.helpText}
                        </Typography>
                    ) : undefined
                )
            );
        default:
            return (
                <Box key={item.linkId} data-linkid={item.linkId} sx={{ ml: indent, mb: verticalSpacing }}>
                    <Typography variant="body1" color="text.secondary">
                        Tipo de campo não suportado: {item.type}
                    </Typography>
                </Box>
            );
    }
};


